import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import {
  FaExclamationTriangle,
  FaCheckCircle,
  FaChevronLeft,
  FaChevronRight,
  FaSearch,
  FaDatabase,
  FaUpload,
  FaFileCode,
  FaClipboard,
  FaDownload,
  FaTrash,
  FaTimes,
  FaCodeBranch,
  FaCloudDownloadAlt,
  FaFileCsv,
} from "react-icons/fa";
import Pagination from "rc-pagination";
import "rc-pagination/assets/index.css";
import AppLayout from "../layout/AppLayout";
import Loader from "../loader/loader";
import regenerateToken from "../regenerateToken";
import "./unmappedManagement.css";
import { buildRequestConfig } from '../../utils/apiHelper';
import { isAdmin as checkAdmin } from '../../utils/authUtils';
import ngTestNames from '../../assets/ngTestNames';

const SORTED_NG_NAMES = [...ngTestNames].sort();

const API_BASE = "https://rnxsohimg1.execute-api.ap-south-1.amazonaws.com/Production";
const VALIDATION_API = "https://44jsabw2qd.execute-api.ap-south-1.amazonaws.com/prod/validateInput";
const MAPPING_API = "https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/mapping/parameter";
const PROFILE_API = "https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/mapping/profile";
const COUNT_PER_PAGE = 10;

/**
 * Convert unmapped object from POST response to rows.
 * Format: { "002990": ["Anti - CCP...", null] }
 * Key = Test ID, Value[0] = Client Name, Value[1] = NG Standard Name (null for unmapped)
 */
// ─── Searchable NG Name Combo Box ───────────────────────────────────────────
const NgNameSelector = ({ currentValue, onSelect }) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const wrapperRef = useRef(null);

  const filtered = useMemo(() => {
    if (!query.trim()) return SORTED_NG_NAMES;
    const q = query.toLowerCase();
    return SORTED_NG_NAMES.filter((n) => n.toLowerCase().includes(q));
  }, [query]);

  // Compute fixed-position coords so the dropdown escapes all overflow:hidden parents
  const computeStyle = () => {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const BUFFER = 8;
    const MAX = 220;
    const WIDTH = 280;
    const spaceBelow = window.innerHeight - rect.bottom - BUFFER;
    const spaceAbove = rect.top - BUFFER;
    if (spaceBelow >= 120 || spaceBelow >= spaceAbove) {
      setDropdownStyle({
        position: "fixed",
        top: rect.bottom + 4,
        left: rect.left,
        width: WIDTH,
        maxHeight: Math.min(MAX, spaceBelow),
      });
    } else {
      setDropdownStyle({
        position: "fixed",
        top: rect.top - 4 - Math.min(MAX, spaceAbove),
        left: rect.left,
        width: WIDTH,
        maxHeight: Math.min(MAX, spaceAbove),
      });
    }
  };

  const openDropdown = () => {
    computeStyle();
    setOpen(true);
  };

  // Recompute on scroll/resize so dropdown tracks the input while open
  useEffect(() => {
    if (!open) return;
    const update = () => computeStyle();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Close on outside click
  useEffect(() => {
    const handleOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    if (open) document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  const handleSelect = (name) => {
    onSelect(name);
    setOpen(false);
    setQuery("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") { setOpen(false); setQuery(""); }
    if (e.key === "Enter" && filtered.length > 0) handleSelect(filtered[0]);
  };

  return (
    <div className="um-ng-selector" ref={wrapperRef}>
      {currentValue ? (
        <div className="um-ng-selected">
          <span>{currentValue}</span>
          <button
            type="button"
            className="um-ng-clear"
            onClick={() => { onSelect(""); }}
            title="Clear selection"
          >
            <FaTimes size={10} />
          </button>
        </div>
      ) : (
        <input
          type="text"
          className="um-ng-input"
          placeholder={`Search ${SORTED_NG_NAMES.length} NG names…`}
          value={query}
          onChange={(e) => { setQuery(e.target.value); openDropdown(); }}
          onFocus={openDropdown}
          onKeyDown={handleKeyDown}
        />
      )}
      {open && !currentValue && (
        <div className="um-ng-dropdown" style={dropdownStyle}>
          {filtered.length > 0 ? (
            filtered.map((name) => (
              <div
                key={name}
                className="um-ng-option"
                onMouseDown={() => handleSelect(name)}
              >
                {name}
              </div>
            ))
          ) : (
            <div className="um-ng-option um-ng-option--empty">No matches found</div>
          )}
        </div>
      )}
    </div>
  );
};

const unmappedToRows = (unmapped) => {
  if (!unmapped || typeof unmapped !== "object") return [];
  return Object.entries(unmapped).map(([testId, value]) => {
    // Handle both array format ["ClientName", ngName] and legacy plain string format
    const clientName = Array.isArray(value)
      ? value[0] || ""
      : typeof value === "string"
      ? value
      : "";
    const ngName = Array.isArray(value) ? value[1] || "" : "";
    return {
      id: testId,
      clientName,
      ngName,
      status: "unmapped",
    };
  });
};

/**
 * Convert mapping object to rows.
 * Format: { "1945": ["Creatinine", "Serum Creatinine"] }
 * Key = Test ID, Value[0] = Client Name, Value[1] = NG Standard Name
 */
const mappingToRows = (mapping) => {
  if (!mapping || typeof mapping !== "object") return [];
  return Object.entries(mapping).map(([testId, value]) => {
    const clientName = Array.isArray(value) ? value[0] || "" : "";
    const ngName = Array.isArray(value) ? value[1] || "" : "";
    return {
      id: testId,
      clientName,
      ngName,
      status: "mapped",
    };
  });
};

/** Simple CSV parser */
const parseCSV = (csvText) => {
  const lines = csvText.split(/\r?\n/).map(line => line.trim()).filter(line => line);
  if (lines.length < 2) throw new Error("CSV must contain headers and at least one data row.");

  const splitLine = (line) => {
    const result = [];
    let cur = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      if (line[i] === '"') {
        // Handle escaped double-quotes ("") logic correctly inside quotes
        if (inQuotes && line[i + 1] === '"') {
          cur += '"';
          i++; // Skip the next quote since it's escaped
        } else {
          inQuotes = !inQuotes; // Toggle simple boundary quoting
        }
      } else if (line[i] === ',' && !inQuotes) {
        result.push(cur.trim());
        cur = '';
      } else {
        cur += line[i];
      }
    }
    result.push(cur.trim());
    return result;
  };

  const headers = splitLine(lines[0]).map(h => h.toLowerCase().replace(/['"]/g, ''));
  const nameIdx = headers.findIndex(h => h === 'name' || /^\s*name\s*$/.test(h));
  const idIdx = headers.findIndex(h => /^\s*(id|identifier|code)\s*$/.test(h));

  if (nameIdx === -1) {
    throw new Error("CSV must contain a 'Name' column.");
  }

  // --- Validation pass: collect all rows with missing Name or ID ---
  const missingNameRows = [];
  const missingIdRows = [];

  for (let i = 1; i < lines.length; i++) {
    const cols = splitLine(lines[i]);
    const rawName = (cols[nameIdx] || '').replace(/^['"]|['"]$/g, '').trim();
    const rawId = idIdx !== -1 ? (cols[idIdx] || '').replace(/^['"]|['"]$/g, '').trim() : '';

    if (!rawName) missingNameRows.push(i); // row number (1-indexed in the data; 2-indexed in file)
    if (!rawId) missingIdRows.push(i);
  }

  const errors = [];
  if (missingNameRows.length > 0) {
    errors.push(
      `Missing Name in ${missingNameRows.length === 1 ? 'row' : 'rows'}: ${missingNameRows.map(r => `#${r}`).join(', ')}.`
    );
  }
  if (missingIdRows.length > 0) {
    errors.push(
      `Missing ID in ${missingIdRows.length === 1 ? 'row' : 'rows'}: ${missingIdRows.map(r => `#${r}`).join(', ')}.`
    );
  }

  if (errors.length > 0) {
    throw new Error(
      `CSV validation failed — please fix the following issues before processing:\n${errors.join('\n')}`
    );
  }
  // --- End validation ---

  const result = {};
  for (let i = 1; i < lines.length; i++) {
    const cols = splitLine(lines[i]);
    let name = cols[nameIdx];
    if (name) {
      name = name.replace(/^["']|["']$/g, '');
      let id = "";
      if (idIdx !== -1 && cols[idIdx]) {
        id = cols[idIdx].replace(/^["']|["']$/g, '');
      }
      result[name] = id;
    }
  }
  return result;
};

const UnmappedManagement = () => {
  // Input method: 'file' | 'paste' | 'fetch' | 'csv'
  const [inputMethod, setInputMethod] = useState("file");

  const downloadSampleCSV = () => {
    const csvContent = "Name,ID\nGlucose Fasting,GLU-F\nCholesterol Total,CHOL-T\nCalcium,\nVitamin D,VIT-D-123";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "sample_unmapped_tests.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Cleanup URL memory leak
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 100);
  };

  // File upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  // JSON paste state
  const [jsonText, setJsonText] = useState("");

  // Fetch-from-API state
  const [fetchResponse, setFetchResponse] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // API state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [postResponse, setPostResponse] = useState(null);

  // Validation state
  const [validationStatus, setValidationStatus] = useState(null); // null | 'validating' | 'valid' | 'invalid'
  const [validationError, setValidationError] = useState(null);
  const [validatedData, setValidatedData] = useState(null);

  // Display state
  const [search, setSearch] = useState("");
  const [filterMode, setFilterMode] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  // Merge state
  const [mergeLoading, setMergeLoading] = useState(false);
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [mergeSummary, setMergeSummary] = useState(null); // { updates: [], additions: [], unchanged: [], finalPayload: {} }
  const [mergeError, setMergeError] = useState(null);
  const [mergeSuccess, setMergeSuccess] = useState(null);

  // CSV validation error modal state (separate from general error screen)
  const [csvError, setCsvError] = useState(null);

  // Profile mapping state — fetched silently when results appear
  const [profileMapping, setProfileMapping] = useState({});

  // Fetch profile mapping whenever postResponse arrives
  useEffect(() => {
    if (!postResponse) return;
    const fetchProfile = async () => {
      try {
        // ── ADMIN PATH: use admin token + inject clientId if a client is selected
        if (checkAdmin()) {
          const res = await axios(buildRequestConfig({ method: "GET", url: PROFILE_API }));
          if (res.data?.isSuccess) {
            setProfileMapping(res.data?.data?.profileMapping || {});
          }
          return;
        }
        // ── CLIENT PATH (unchanged) ──────────────────────────────────────────
        let idToken = Cookies.get("idToken");
        let res = await axios({
          method: "GET",
          url: PROFILE_API,
          headers: { authorization: idToken },
          validateStatus: (s) => s >= 200 && s <= 401,
        });
        if (res.status === 401) {
          await regenerateToken();
          idToken = Cookies.get("idToken");
          res = await axios({
            method: "GET",
            url: PROFILE_API,
            headers: { authorization: idToken },
            validateStatus: (s) => s >= 200 && s <= 401,
          });
        }
        if (res.data?.isSuccess) {
          setProfileMapping(res.data?.data?.profileMapping || {});
        }
      } catch (e) {
        // Silent fail — profile column simply shows "—" if unavailable
        console.warn("Failed to load profile mapping for display:", e.message);
      }
    };
    fetchProfile();
  }, [postResponse]);

  // Handle input method change
  const handleInputMethodChange = (method) => {
    setInputMethod(method);
    // Reset validation when switching modes
    setValidationStatus(null);
    setValidationError(null);
    setValidatedData(null);
    setError(null);
    // Reset fetch state when switching to or away from fetch
    setFetchResponse(null);
    setFetchLoading(false);
    setFetchError(null);
  };

  // Fetch unmapped tests from API (GET) using org/center from credentials
  const handleFetch = useCallback(async () => {
    let org = "";
    let center = "";

    // ── ADMIN PATH: read org/center from the selected client in sessionStorage
    try {
      const adminClient = sessionStorage.getItem("adminSelectedClient");
      if (adminClient) {
        const parsed = JSON.parse(adminClient);
        org = parsed?.org || "";
        center = parsed?.center || "";
      }
    } catch (e) {
      console.warn("Failed to read adminSelectedClient from sessionStorage", e);
    }

    // ── CLIENT PATH (fallback): read from localStorage credentials
    if (!org || !center) {
      try {
        const credentials = JSON.parse(localStorage.getItem("credentials") || "{}");
        org = credentials.org || "";
        center = credentials.center || "";
      } catch (e) {
        console.error("Failed to parse credentials", e);
      }
    }

    if (!org || !center) {
      setFetchError("Organization and center are required. Please log in again or check your credentials.");
      return;
    }


    setFetchError(null);
    setFetchResponse(null);
    setFetchLoading(true);

    const url = `${API_BASE}/unmappedmanagement?org=${encodeURIComponent(org)}&center=${encodeURIComponent(center)}`;
    let idToken = Cookies.get("idToken");

    try {
      let response = await axios({
        method: "GET",
        url,
        headers: { Authorization: idToken },
        validateStatus: (status) => status >= 200 && status <= 401,
      });

      if (response.status === 401) {
        await regenerateToken();
        idToken = Cookies.get("idToken");
        response = await axios({
          method: "GET",
          url,
          headers: { Authorization: idToken },
          validateStatus: (status) => status >= 200 && status <= 401,
        });
      }

      const data = response.data;
      if (data?.isSuccess) {
        setFetchResponse(data);
        setFetchError(null);
      } else {
        const errStr = data?.error;
        setFetchError(typeof errStr === "object" ? JSON.stringify(errStr) : errStr || "Failed to fetch unmapped tests.");
        setFetchResponse(null);
      }
    } catch (err) {
      const status = err?.response?.status;
      let msg = err?.response?.data?.error || err?.message || "Network error.";
      if (status === 401) msg = "Session expired. Please log in again.";
      if (status === 403) msg = "Access denied.";
      setFetchError(typeof msg === "object" ? JSON.stringify(msg) : msg);
      setFetchResponse(null);
    } finally {
      setFetchLoading(false);
    }
  }, []);

  // Validate JSON data with API
  const validateJSON = async (jsonData) => {
    setValidationStatus('validating');
    setValidationError(null);
    setError(null);

    try {
      const config = {
        headers: { "Content-Type": "application/json" },
      };

      const response = await axios.post(VALIDATION_API, jsonData, config);

      if (response.data?.isSuccess) {
        setValidationStatus('valid');
        setValidatedData(jsonData);
        setValidationError(null);
      } else {
        setValidationStatus('invalid');
        const apiError = response.data?.error;
        let errorMessage = "Validation failed. Please check your JSON structure.";
        if (apiError) {
          errorMessage = typeof apiError === 'string' ? apiError : JSON.stringify(apiError);
        }
        setValidationError(errorMessage);
        setValidatedData(null);
      }
    } catch (err) {
      setValidationStatus('invalid');
      let errorMsg = err?.response?.data?.error || err?.message || "Unable to validate. Please check your connection.";
      if (typeof errorMsg === 'object') errorMsg = JSON.stringify(errorMsg);
      setValidationError(errorMsg);
      setValidatedData(null);
    }
  };

  // Manual validation trigger
  const handleValidateClick = async () => {
    try {
      let rawText;

      if (inputMethod === 'file') {
        if (!selectedFile) {
          setError("Please select a JSON file.");
          return;
        }
        rawText = await selectedFile.text();
      } else {
        if (!jsonText.trim()) {
          setError("Please paste JSON content.");
          return;
        }
        rawText = jsonText;
      }

      let payload;
      try {
        payload = JSON.parse(rawText);
      } catch {
        // If client-side parse fails, send raw text to the API for server-side validation
        payload = rawText;
      }

      await validateJSON(payload);
    } catch (err) {
      setValidationStatus('invalid');
      setValidationError(err.message || "Error processing JSON.");
    }
  };

  // Handle file selection (NO auto-validation)
  const handleFileSelect = async (file) => {
    if (file) {
      if (inputMethod === 'csv') {
        const isValidCsv = file.type === "text/csv" || file.type === "application/vnd.ms-excel" || file.name.endsWith(".csv");
        if (isValidCsv) {
          setSelectedFile(file);
          setError(null);
          setValidationStatus(null);
          setValidationError(null);
          setValidatedData(null);
        } else {
          setError("Please select a valid CSV file.");
          setSelectedFile(null);
          setValidationStatus(null);
          setValidationError(null);
          setValidatedData(null);
        }
      } else {
        if (file.type === "application/json") {
          setSelectedFile(file);
          setError(null);
          setValidationStatus(null);
          setValidationError(null);
          setValidatedData(null);
        } else {
          setError("Please select a valid JSON file.");
          setSelectedFile(null);
          setValidationStatus(null);
          setValidationError(null);
          setValidatedData(null);
        }
      }
    }
  };

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  // Handle drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  // Handle file input change
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };


  // Process and submit JSON
  const handleSubmit = async () => {
    // Fetch path: use fetched response, no validation or transformation
    if (inputMethod === "fetch") {
      if (!fetchResponse?.isSuccess || !fetchResponse?.data) {
        setError("Please fetch data first.");
        return;
      }
      setError(null);
      setLoading(true);
      setPostResponse(null);
      try {
        let credentials = {};
        try {
          credentials = JSON.parse(localStorage.getItem("credentials") || "{}");
        } catch (e) {
          console.error("Failed to parse credentials", e);
        }
        const rawUnmapped = fetchResponse.data?.unmapped || {};
        const payload = {
          org: credentials.org || "",
          center: credentials.center || "",
          unmappedData: { raw_unmapped: rawUnmapped },
        };
        const url = `${API_BASE}/unmappedmanagement`;
        const config = { headers: { "Content-Type": "application/json" } };
        const response = await axios.post(url, payload, config);
        if (response.data?.isSuccess) {
          setPostResponse(response.data.data);
        } else {
          let errStr = response.data?.error;
          if (typeof errStr === "object") errStr = JSON.stringify(errStr);
          setError(errStr || "Failed to process unmapped tests.");
        }
      } catch (err) {
        const status = err?.response?.status;
        let msg = err?.response?.data?.error || err?.message || "Network error.";
        if (status === 401) msg = "Session expired. Please log in again.";
        if (status === 403) msg = "Access denied.";
        setError(typeof msg === "object" ? JSON.stringify(msg) : msg);
      } finally {
        setLoading(false);
      }
      return;
    }

    // File/paste/csv path: read directly from input (no validation required)
    let jsonData;
    let parsedCsvMap = null;
    try {
      let rawText;
      if (inputMethod === 'file') {
        if (!selectedFile) {
          setError("Please select a JSON file.");
          return;
        }
        rawText = await selectedFile.text();
      } else if (inputMethod === 'csv') {
        if (!selectedFile) {
          setError("Please select a CSV file.");
          return;
        }
        rawText = await selectedFile.text();
      } else {
        if (!jsonText.trim()) {
          setError("Please paste JSON content.");
          return;
        }
        rawText = jsonText;
      }

      if (inputMethod === 'csv') {
        try {
          parsedCsvMap = parseCSV(rawText);
        } catch (e) {
          // Show CSV validation errors in the dedicated popup modal, not the negative screen
          setCsvError(e.message || "Invalid CSV format.");
          return;
        }
      } else {
        try {
          jsonData = JSON.parse(rawText);
        } catch {
          setError("Invalid JSON format. Please ensure your input is valid JSON.");
          return;
        }
      }
    } catch (err) {
      setError(err.message || "Error reading input.");
      return;
    }

    setError(null);
    setLoading(true);
    setPostResponse(null);

    try {
      let credentials = {};
      try {
        credentials = JSON.parse(localStorage.getItem("credentials") || "{}");
      } catch (e) {
        console.error("Failed to parse credentials", e);
      }

      // Transform: extract parameter names from results -> investigation -> observations
      let rawUnmapped = {};

      if (inputMethod === 'csv') {
        rawUnmapped = parsedCsvMap;
      } else {
        // Helper: process investigation array -> each item has "observations" array
        const processInvestigation = (investigationArr) => {
          if (!Array.isArray(investigationArr)) return;
          investigationArr.forEach((test) => {
            if (test.observations && Array.isArray(test.observations)) {
              test.observations.forEach((obs) => {
                const paramName = obs.name;
                const paramId = obs.id;
                if (paramName) {
                  rawUnmapped[paramName] = paramId || "";
                }
              });
            }
          });
        };

        if (jsonData?.results) {
          if (Array.isArray(jsonData.results)) {
            jsonData.results.forEach((resObj) => {
              if (resObj.investigation && Array.isArray(resObj.investigation)) {
                processInvestigation(resObj.investigation);
              }
            });
          } else if (typeof jsonData.results === "object" && jsonData.results.investigation) {
            processInvestigation(jsonData.results.investigation);
          }
        }
      }

      // Restructure payload
      const payload = {
        org: credentials.org || "",
        center: credentials.center || "",
        unmappedData: {
          raw_unmapped: rawUnmapped,
        },
      };

      // Call POST API
      const url = `${API_BASE}/unmappedmanagement`;

      const config = {
        headers: {
          "Content-Type": "application/json",
        },
      };

      const response = await axios.post(url, payload, config);

      if (response.data?.isSuccess) {
        setPostResponse(response.data.data);
      } else {
        let errStr = response.data?.error;
        if (typeof errStr === 'object') errStr = JSON.stringify(errStr);
        setError(errStr || "Failed to process unmapped tests.");
      }
    } catch (err) {
      const status = err?.response?.status;
      let msg = err?.response?.data?.error || err?.message || "Network error.";

      if (err.name === "SyntaxError") {
        msg = inputMethod === 'csv' ? "Invalid CSV format. Please check your input." : "Invalid JSON format. Please check your input.";
      } else if (status === 401) {
        msg = "Session expired. Please log in again.";
      } else if (status === 403) {
        msg = "Access denied. You may not have permission to access this resource.";
      }

      if (typeof msg === 'object') {
        msg = JSON.stringify(msg);
      }

      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Pending NG name selections — staged before the user confirms with tick
  const [pendingNgNames, setPendingNgNames] = useState({});

  // Called when user picks a name from the dropdown (does NOT move row yet)
  const handlePendingSelect = useCallback((testId, name) => {
    setPendingNgNames((prev) => ({ ...prev, [testId]: name }));
  }, []);

  // Called when user clicks the tick — NOW moves row to mapped
  const handleAssignNgName = useCallback((testId, clientName, selectedNgName) => {
    if (!postResponse) return;
    const updatedUnmapped = { ...postResponse.unmapped };
    const updatedMapping = { ...postResponse.mapping };
    if (selectedNgName) {
      delete updatedUnmapped[testId];
      updatedMapping[testId] = [clientName, selectedNgName];
    } else {
      // Clear: move back to unmapped
      delete updatedMapping[testId];
      updatedUnmapped[testId] = [clientName, null];
    }
    // Remove from pending after confirming
    setPendingNgNames((prev) => { const next = { ...prev }; delete next[testId]; return next; });
    setPostResponse({ ...postResponse, unmapped: updatedUnmapped, mapping: updatedMapping });
  }, [postResponse]);

  // Combine unmapped and mapped rows
  const allRows = useMemo(() => {
    if (!postResponse) return [];

    const unmappedRows = unmappedToRows(postResponse.unmapped || {});
    const mappedRows = mappingToRows(postResponse.mapping || {});

    return [...unmappedRows, ...mappedRows];
  }, [postResponse]);

  // Filter rows
  const filteredRows = useMemo(() => {
    let list = allRows;

    if (filterMode === "mapped") {
      list = allRows.filter((r) => r.status === "mapped");
    } else if (filterMode === "unmapped") {
      list = allRows.filter((r) => r.status === "unmapped");
    }

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (r) =>
          String(r.id || "").toLowerCase().includes(q) ||
          String(r.clientName || "").toLowerCase().includes(q) ||
          String(r.ngName || "").toLowerCase().includes(q)
      );
    }

    return list;
  }, [allRows, filterMode, search]);

  // Paginate rows
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * COUNT_PER_PAGE;
    return filteredRows.slice(start, start + COUNT_PER_PAGE);
  }, [filteredRows, currentPage]);

  // Calculate stats
  const stats = useMemo(() => {
    const total = allRows.length;
    const unmapped = allRows.filter((r) => r.status === "unmapped").length;
    const mapped = allRows.filter((r) => r.status === "mapped").length;
    return { total, unmapped, mapped };
  }, [allRows]);

  // Download testsToMap as JSON
  const handleDownloadTestsToMap = () => {
    if (!postResponse?.testsToMap) return;

    const dataStr = JSON.stringify(postResponse.testsToMap, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "tests_to_map.json";
    link.click();
    URL.revokeObjectURL(url);
  };

  // Move a mapped row back to unmapped (strips ngName/profile, keeps testId + clientName)
  const handleDeleteRow = (rowId) => {
    if (!postResponse || !postResponse.mapping) return;
    const valueArr = postResponse.mapping[rowId];
    const clientName = Array.isArray(valueArr) ? valueArr[0] || "" : "";
    const newMapping = { ...postResponse.mapping };
    delete newMapping[rowId];
    const newUnmapped = { ...postResponse.unmapped, [rowId]: [clientName, null] };
    setPostResponse({ ...postResponse, mapping: newMapping, unmapped: newUnmapped });
  };

  // Merge: Fetch existing mapping, compare, show review modal
  const handleMerge = async () => {
    setMergeLoading(true);
    setMergeError(null);
    setMergeSuccess(null);

    try {
      // Step 1: Fetch existing mapping from server
      const idToken = Cookies.get("idToken");
      let existingResponse;
      try {
        // ── ADMIN PATH: use admin token + inject clientId if a client is selected
        if (checkAdmin()) {
          existingResponse = await axios(buildRequestConfig({ method: "GET", url: MAPPING_API }));
        } else {
          // ── CLIENT PATH (unchanged) ──────────────────────────────────────────
          const idToken = Cookies.get("idToken");
          existingResponse = await axios({
            headers: { authorization: idToken },
            method: "GET",
            url: MAPPING_API,
            validateStatus: (status) => status >= 200 && status <= 401,
          });

          if (existingResponse.status === 401) {
            await regenerateToken();
            const newToken = Cookies.get("idToken");
            existingResponse = await axios({
              headers: { authorization: newToken },
              method: "GET",
              url: MAPPING_API,
              validateStatus: (status) => status >= 200 && status <= 401,
            });
          }
        }
      } catch (fetchErr) {
        setMergeError('Failed to fetch existing mappings from server. Please check your connection.');
        setMergeLoading(false);
        return;
      }

      // Step 2: Extract server data (format: { ID: Name })
      const serverIdToName = existingResponse.data?.data?.idMapping || {};

      // Step 3: Build server Name->IDs map for easy lookup
      const serverNameToIds = {};
      Object.entries(serverIdToName).forEach(([id, name]) => {
        if (!serverNameToIds[name]) serverNameToIds[name] = [];
        serverNameToIds[name].push(id);
      });

      // Step 4: Get local mapping from postResponse.mapping
      // Format: { ID: [ClientName, NGStandardName] }
      const localMapping = postResponse?.mapping || {};

      // Step 5: Compare and build summary
      const updates = [];   // { name, oldId, newId }
      const additions = []; // { name, newId }
      const unchanged = []; // { name, id }

      const finalPayload = { ...serverIdToName };

      Object.entries(localMapping).forEach(([newId, valueArr]) => {
        const ngStandardName = Array.isArray(valueArr) ? valueArr[1] : null;
        if (!ngStandardName) return;

        if (serverNameToIds[ngStandardName]) {
          const oldIds = serverNameToIds[ngStandardName];
          const oldId = oldIds[0];
          if (oldId !== newId) {
            updates.push({ name: ngStandardName, oldId, newId });
            oldIds.forEach((oid) => delete finalPayload[oid]);
            finalPayload[newId] = ngStandardName;
          } else {
            unchanged.push({ name: ngStandardName, id: oldId });
          }
        } else {
          additions.push({ name: ngStandardName, newId });
          finalPayload[newId] = ngStandardName;
        }
      });

      setMergeSummary({ updates, additions, unchanged, finalPayload });
      setMergeModalOpen(true);

    } catch (err) {
      setMergeError(err.message || 'An unexpected error occurred during merge.');
    } finally {
      setMergeLoading(false);
    }
  };

  // Confirm merge: POST the final payload
  const handleConfirmMerge = async () => {
    if (!mergeSummary?.finalPayload) return;

    setMergeLoading(true);
    setMergeError(null);

    try {
      let response;
      // ── ADMIN PATH: use admin token + inject clientId if a client is selected
      if (checkAdmin()) {
        response = await axios(buildRequestConfig({
          method: "POST",
          url: MAPPING_API,
          data: mergeSummary.finalPayload,
        }));
        if (response.status === 401) {
          await regenerateToken();
          response = await axios(buildRequestConfig({
            method: "POST",
            url: MAPPING_API,
            data: mergeSummary.finalPayload,
          }));
        }
      } else {
        // ── CLIENT PATH (unchanged) ──────────────────────────────────────────
        const idToken = Cookies.get("idToken");
        const config = {
          url: MAPPING_API,
          headers: { authorization: idToken },
          method: "POST",
          validateStatus: (status) => status >= 200 && status <= 401,
          data: mergeSummary.finalPayload,
        };
        response = await axios(config);
        if (response.status === 401) {
          await regenerateToken();
          const newToken = Cookies.get("idToken");
          config.headers.authorization = newToken;
          response = await axios(config);
        }
      }

      if (response.data?.isSuccess) {
        // Update global mapping (unmappedmanagement/updatemapping)
        const mappingdata =
          postResponse?.finalMapping && typeof postResponse.finalMapping === "object"
            ? postResponse.finalMapping
            : {};
        const updateMappingUrl = `${API_BASE}/unmappedmanagement/updatemapping`;
        let updateMappingResponse;
        let secondaryFailed = false;

        let latestToken = Cookies.get("idToken");

        try {
          updateMappingResponse = await axios({
            url: updateMappingUrl,
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: latestToken },
            data: { mappingdata },
            validateStatus: (status) => status >= 200 && status <= 401,
          });

          if (updateMappingResponse.status === 401) {
            await regenerateToken();
            latestToken = Cookies.get("idToken");
            updateMappingResponse = await axios({
              url: updateMappingUrl,
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: latestToken },
              data: { mappingdata },
              validateStatus: (status) => status >= 200 && status <= 401,
            });
          }
        } catch (updateErr) {
          const msg =
            updateErr?.response?.data?.error ||
            updateErr?.message ||
            "Global mapping update failed.";
          setMergeError(typeof msg === "object" ? JSON.stringify(msg) : msg);
          secondaryFailed = true;
        }

        if (!secondaryFailed && updateMappingResponse && !updateMappingResponse?.data?.isSuccess) {
          const errMsg = updateMappingResponse?.data?.error || "Global mapping update failed.";
          setMergeError(typeof errMsg === "object" ? JSON.stringify(errMsg) : errMsg);
          secondaryFailed = true;
        }

        // Only mark as success when the secondary API also succeeded
        if (!secondaryFailed) {
          setMergeSuccess(
            `Merge successful! ${mergeSummary.updates.length} updated, ${mergeSummary.additions.length} added.`
          );
          setMergeModalOpen(false);
          setMergeSummary(null);
        }
      } else {
        let errMsg = response.data?.error || 'Failed to save merged mappings.';
        if (typeof errMsg === 'object') errMsg = JSON.stringify(errMsg);
        setMergeError(errMsg);
      }
    } catch (err) {
      setMergeError(err.message || 'Failed to save merged mappings.');
    } finally {
      setMergeLoading(false);
    }
  };

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterMode, search]);

  // Auto-fetch when user switches to "Fetch from API" tab
  useEffect(() => {
    if (inputMethod === "fetch") {
      handleFetch();
    }
  }, [inputMethod, handleFetch]);

  return (
    <AppLayout>
      <div className="um-container">
        {loading && (
          <div className="um-loader-wrap">
            <Loader />
          </div>
        )}

        <div className="um-hero">
          <div className="um-hero-content">
            <div className="um-hero-icon">
              <FaDatabase />
            </div>
            <div className="um-hero-text">
              <h1>Auto Parameter Mapping</h1>
              <p>Upload or paste JSON to process and view unmapped test mappings.</p>
            </div>
          </div>
          <div className="um-hero-accent" />
        </div>

        {/* Input Section */}
        <div className="um-upload-section">
          <div className="um-input-tabs">
            <button
              type="button"
              className={`um-tab ${inputMethod === "file" ? "active" : ""}`}
              onClick={() => handleInputMethodChange("file")}
            >
              <FaUpload /> Upload JSON
            </button>
            <button
              type="button"
              className={`um-tab ${inputMethod === "csv" ? "active" : ""}`}
              onClick={() => handleInputMethodChange("csv")}
            >
              <FaFileCsv /> Upload CSV
            </button>
            <button
              type="button"
              className={`um-tab ${inputMethod === "paste" ? "active" : ""}`}
              onClick={() => handleInputMethodChange("paste")}
            >
              <FaClipboard /> Paste JSON
            </button>
            <button
              type="button"
              className={`um-tab ${inputMethod === "fetch" ? "active" : ""}`}
              onClick={() => handleInputMethodChange("fetch")}
            >
              <FaCloudDownloadAlt /> Fetch from API
            </button>
          </div>

          {inputMethod === "file" || inputMethod === "csv" ? (
            <div
              className={`um-dropzone ${dragActive ? "active" : ""} ${selectedFile ? "has-file" : ""}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {inputMethod === "csv" ? (
                <FaFileCsv className="um-dropzone-icon" />
              ) : (
                <FaFileCode className="um-dropzone-icon" />
              )}

              {selectedFile ? (
                <>
                  <p className="um-dropzone-filename">{selectedFile.name}</p>
                  <p className="um-dropzone-size">
                    {(selectedFile.size / 1024).toFixed(2)} KB
                  </p>
                  <button
                    type="button"
                    className="um-change-file-btn"
                    onClick={() => {
                      setSelectedFile(null);
                      setValidationStatus(null);
                      setValidationError(null);
                      setValidatedData(null);
                    }}
                  >
                    Change File
                  </button>
                </>
              ) : (
                <>
                  <p className="um-dropzone-text">
                    Drag & drop your {inputMethod === "csv" ? "CSV" : "JSON"} file here
                  </p>
                  <p className="um-dropzone-subtext">or</p>
                  <label className="um-file-input-label">
                    Browse Files
                    <input
                      type="file"
                      accept={inputMethod === "csv" ? ".csv, text/csv, application/vnd.ms-excel" : "application/json"}
                      onChange={handleFileChange}
                      className="um-file-input"
                    />
                  </label>
                  {inputMethod === "csv" && (
                    <div style={{ marginTop: '1rem' }}>
                      <button
                        type="button"
                        className="um-fetch-refresh-btn"
                        onClick={(e) => { e.stopPropagation(); downloadSampleCSV(); }}
                        title="Download Sample CSV"
                        style={{ backgroundColor: 'transparent', color: '#1890ff', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                      >
                        <FaDownload /> Download Sample CSV
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : inputMethod === "paste" ? (
            <div className="um-paste-section">
              <textarea
                className="um-json-textarea"
                placeholder='Paste your JSON here, e.g.:
{
  "test_001": "Hemoglobin",
  "test_002": "Glucose Fasting",
  "test_003": "TSH"
}'
                value={jsonText}
                onChange={(e) => {
                  setJsonText(e.target.value);
                  // Reset validation when text changes
                  setValidationStatus(null);
                  setValidationError(null);
                  setValidatedData(null);
                }}
                rows={12}
              />
            </div>
          ) : (
            <div className="um-fetch-section um-fetch-card">
              {(fetchLoading || (inputMethod === "fetch" && !fetchResponse && !fetchError)) && (
                <div className="um-fetch-loading">
                  <div className="um-fetch-loading-spinner" />
                  <p className="um-fetch-loading-text">Fetching unmapped tests…</p>
                  <p className="um-fetch-loading-subtext">Using your organization and center</p>
                </div>
              )}
              {!fetchLoading && fetchError && (
                <div className="um-fetch-empty um-fetch-empty-error">
                  <div className="um-fetch-empty-icon-wrap">
                    <FaExclamationTriangle className="um-fetch-empty-icon" />
                  </div>
                  <h3 className="um-fetch-empty-title">Couldn&apos;t load unmapped tests</h3>
                  <p className="um-fetch-empty-message">{fetchError}</p>
                  <button
                    type="button"
                    className="um-fetch-btn um-fetch-retry-btn"
                    onClick={handleFetch}
                  >
                    <FaCloudDownloadAlt /> Try again
                  </button>
                </div>
              )}
              {!fetchLoading && !fetchError && fetchResponse?.isSuccess && (
                <>
                  <div className="um-fetch-result-card">
                    <div className="um-fetch-result-header">
                      <FaCheckCircle className="um-fetch-success-icon" />
                      <div className="um-fetch-result-meta">
                        <span className="um-fetch-result-title">Fetched successfully</span>
                        <span className="um-fetch-result-count">
                          {typeof fetchResponse.data?.unmapped === "object"
                            ? Object.keys(fetchResponse.data.unmapped).length
                            : 0}{" "}
                          unmapped {Object.keys(fetchResponse.data?.unmapped || {}).length === 1 ? "entry" : "entries"}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="um-fetch-refresh-btn"
                        onClick={handleFetch}
                        title="Refresh data"
                      >
                        <FaCloudDownloadAlt /> Refresh
                      </button>
                    </div>
                    {fetchResponse.data && Object.keys(fetchResponse.data).length > 0 ? (
                      <pre className="um-fetch-json">
                        {JSON.stringify(fetchResponse.data, null, 2)}
                      </pre>
                    ) : (
                      <div className="um-fetch-empty um-fetch-empty-success-inline">
                        <FaCheckCircle className="um-fetch-empty-icon" />
                        <p>No unmapped tests for this org/center.</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}



          {/* Validate Button — commented out; validation now skipped */}
          {inputMethod !== "fetch" && inputMethod !== "csv" && ((inputMethod === 'file' && selectedFile) || (inputMethod === 'paste' && jsonText.trim())) && (
            <button
              type="button"
              className="um-validate-btn mt-3"
              onClick={handleValidateClick}
              disabled={validationStatus === 'validating'}
            >
              <FaCheckCircle /> Validate JSON
            </button>
          )}

          {/* Validation Status — commented out; validation now skipped */}
          {inputMethod !== "fetch" && validationStatus && validationStatus !== 'invalid' && (
            <div className={`um-validation-status ${validationStatus}`}>
              {validationStatus === 'validating' && (
                <>
                  <div className="um-validation-spinner"></div>
                  <span>Validating JSON...</span>
                </>
              )}
              {validationStatus === 'valid' && (
                <>
                  <FaCheckCircle className="um-validation-icon success" />
                  <span>✓ JSON is valid and ready to process</span>
                </>
              )}
            </div>
          )}

          <button
            type="button"
            className="um-submit-btn mt-3"
            onClick={handleSubmit}
            disabled={
              loading ||
              (inputMethod !== "fetch" && inputMethod !== "csv" && validationStatus !== "valid") ||
              (inputMethod === "csv" && !selectedFile) ||
              (inputMethod === "fetch" && !fetchResponse?.isSuccess)
            }
          >
            <FaUpload /> {inputMethod === "csv" ? "Process CSV" : "Process JSON"}
          </button>
        </div>

        {/* CSV Validation Error Modal */}
        {csvError && (
          <div className="um-csv-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="um-csv-modal-title">
            <div className="um-csv-modal">
              <div className="um-csv-modal-header">
                <div className="um-csv-modal-title-row">
                  <FaExclamationTriangle className="um-csv-modal-icon" />
                  <h3 id="um-csv-modal-title" className="um-csv-modal-title">CSV Validation Failed</h3>
                </div>
                <button
                  type="button"
                  className="um-csv-modal-close"
                  onClick={() => setCsvError(null)}
                  aria-label="Close error dialog"
                >
                  <FaTimes size={16} />
                </button>
              </div>
              <div className="um-csv-modal-body">
                <p className="um-csv-modal-intro">Please fix the following issues in your CSV file before processing:</p>
                <ul className="um-csv-modal-list">
                  {csvError.split('\n').filter(Boolean).map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ul>
              </div>
              <div className="um-csv-modal-footer">
                <button
                  type="button"
                  className="um-csv-modal-btn"
                  onClick={() => setCsvError(null)}
                >
                  Close &amp; Fix File
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Negative Screen for Errors */}
        {(error || validationStatus === 'invalid') && (
          <div className="um-empty um-empty-error">
            <div className="um-empty-icon-wrap">
              <FaExclamationTriangle className="um-empty-icon" />
            </div>
            <h3 className="um-empty-title">Something went wrong</h3>
            <p className="um-empty-message">
              {error || validationError || "An unexpected error occurred."}
            </p>
            <button
              type="button"
              className="um-change-file-btn um-btn-empty-cta"
              onClick={() => {
                setError(null);
                setValidationStatus(null);
                setValidationError(null);
                // Optional: clear file if it was a file error, or just let user retry
              }}
            >
              Try Again
            </button>
          </div>
        )}

        {/* Hide Error Banner since we use Negative Screen */}
        {/* {error && ...} removed */}

        {/* Results Section */}
        {postResponse && (
          <>
            {/* Stats and Actions */}
            <div className="um-results-header">
              <div className="um-stats-cards">
                <div className="um-stat-card">
                  <div className="um-stat-value">{stats.total}</div>
                  <div className="um-stat-label">Total Tests</div>
                </div>
                <div className="um-stat-card unmapped">
                  <div className="um-stat-value">{stats.unmapped}</div>
                  <div className="um-stat-label">Unmapped</div>
                </div>
                <div className="um-stat-card mapped">
                  <div className="um-stat-value">{stats.mapped}</div>
                  <div className="um-stat-label">Mapped</div>
                </div>
              </div>

            </div>

            {/* Merge Success Message */}
            {mergeSuccess && (
              <div className="um-merge-success-banner mb-3">
                <FaCheckCircle />
                <span>{mergeSuccess}</span>
                <button type="button" onClick={() => setMergeSuccess(null)} className="um-merge-dismiss">
                  <FaTimes size={14} />
                </button>
              </div>
            )}

            {/* Merge Error Message */}
            {mergeError && !mergeModalOpen && (
              <div className="um-merge-error-banner">
                <FaExclamationTriangle />
                <span>{mergeError}</span>
                <button type="button" onClick={() => setMergeError(null)} className="um-merge-dismiss">
                  <FaTimes size={14} />
                </button>
              </div>
            )}

            {/* Search and Filters */}
            {allRows.length > 0 && (
              <>
                <div className="um-controls mb-4 um-controls-secondary">
                  <div className="um-search-wrapper">
                    <FaSearch className="um-search-icon" />
                    <input
                      className="um-search-input"
                      placeholder="Search by test ID or name..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                  {/* Merge Button - only show when there are mapped items */}
                  {stats.mapped > 0 && (
                    <button
                      type="button"
                      className="um-merge-btn"
                      onClick={handleMerge}
                      disabled={mergeLoading}
                    >
                      {mergeLoading ? (
                        <><div className="um-validation-spinner" style={{ width: 16, height: 16 }}></div> Merging...</>
                      ) : (
                        <><FaCodeBranch /> Merge & Update</>
                      )}
                    </button>
                  )}
                  <div className="um-filters">

                    <button
                      type="button"
                      className={`um-filter-option ${filterMode === "all" ? "active" : ""}`}
                      onClick={() => setFilterMode("all")}
                    >
                      All
                    </button>
                    <button
                      type="button"
                      className={`um-filter-option ${filterMode === "unmapped" ? "active" : ""}`}
                      onClick={() => setFilterMode("unmapped")}
                    >
                      Unmapped Only
                    </button>
                    <button
                      type="button"
                      className={`um-filter-option ${filterMode === "mapped" ? "active" : ""}`}
                      onClick={() => setFilterMode("mapped")}
                    >
                      Mapped Only
                    </button>
                  </div>
                </div>

                {/* Table Header */}
                <div className="um-list-header">
                  <div>Test ID</div>
                  <div>Client Name</div>
                  <div>NG Standard Name</div>
                  <div>Profile</div>
                  <div className="um-status-col">Status</div>
                  <div className="um-action-col">Action</div>
                </div>

                {/* Table Body */}
                <div className="um-list-container">
                  {paginatedRows.length > 0 ? (
                    paginatedRows.map((row, idx) => (
                      <div className="um-row" key={`${row.id}-${idx}`}>
                        <div className="um-test-id">{row.id}</div>
                        <div className="um-test-name">{row.clientName}</div>
                        <div className="um-test-name um-ng-cell">
                          {row.status === "unmapped" ? (
                            <NgNameSelector
                              currentValue={pendingNgNames[row.id] || ""}
                              onSelect={(name) => handlePendingSelect(row.id, name)}
                            />
                          ) : (
                            row.ngName || "—"
                          )}
                        </div>
                        <div className="um-test-name">
                          {profileMapping[row.status === "unmapped" ? pendingNgNames[row.id] : row.ngName]
                            ? <span className="um-profile-tag">{profileMapping[row.status === "unmapped" ? pendingNgNames[row.id] : row.ngName]}</span>
                            : <span className="um-profile-none">—</span>
                          }
                        </div>
                        <div className="um-status-col">
                          <span className={`um-badge um-badge-${row.status}`}>
                            {row.status === "mapped" ? (
                              <>
                                <FaCheckCircle size={12} /> Mapped
                              </>
                            ) : (
                              <>
                                <FaExclamationTriangle size={12} /> Unmapped
                              </>
                            )}
                          </span>
                        </div>
                        <div className="um-action-col">
                          {row.status === "mapped" && (
                            <button
                              type="button"
                              className="um-delete-btn"
                              onClick={() => handleDeleteRow(row.id)}
                              title={`Delete mapping for ${row.clientName}`}
                            >
                              <FaTrash size={13} />
                            </button>
                          )}
                          {row.status === "unmapped" && pendingNgNames[row.id] && (
                            <button
                              type="button"
                              className="um-confirm-btn"
                              onClick={() => handleAssignNgName(row.id, row.clientName, pendingNgNames[row.id])}
                              title={`Confirm mapping to ${pendingNgNames[row.id]}`}
                            >
                              <FaCheckCircle size={15} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="um-empty-inline">
                      No parameters match your search or filter.
                    </div>
                  )}
                </div>

                {/* Pagination */}
                <Pagination
                  onChange={(p) => setCurrentPage(p)}
                  pageSize={COUNT_PER_PAGE}
                  total={filteredRows.length}
                  current={currentPage}
                  showTitle={false}
                  itemRender={(current, type, element) => {
                    if (type === "prev")
                      return (
                        <span className="um-pagination-icon">
                          <FaChevronLeft />
                        </span>
                      );
                    if (type === "next")
                      return (
                        <span className="um-pagination-icon">
                          <FaChevronRight />
                        </span>
                      );
                    return element;
                  }}
                />
              </>
            )}

            {/* Empty State */}
            {allRows.length === 0 && (
              <div className="um-empty um-empty-success">
                <div className="um-empty-icon-wrap">
                  <FaCheckCircle className="um-empty-icon" />
                </div>
                <h3 className="um-empty-title">All tests mapped</h3>
                <p className="um-empty-message">
                  All parameters in the uploaded JSON are successfully mapped.
                </p>
              </div>
            )}
          </>
        )}

        {/* ===== Merge Review Modal ===== */}
        {mergeModalOpen && mergeSummary && (
          <ModalWithA11y
            onClose={() => { setMergeModalOpen(false); setMergeError(null); }}
            mergeLoading={mergeLoading}
            mergeError={mergeError}
            mergeSummary={mergeSummary}
            handleConfirmMerge={handleConfirmMerge}
          />
        )}
      </div>
    </AppLayout>
  );
};

// ─── Accessible Merge Modal ───────────────────────────────────────────────────
const ModalWithA11y = ({ onClose, mergeLoading, mergeError, mergeSummary, handleConfirmMerge }) => {
  const modalRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    // Store the element that had focus before modal opened
    previousFocusRef.current = document.activeElement;

    // Move focus into the modal
    const firstFocusable = modalRef.current?.querySelector(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (firstFocusable) firstFocusable.focus();

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Tab') {
        const focusable = Array.from(
          modalRef.current?.querySelectorAll(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          ) || []
        );
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Return focus to the element that opened the modal
      if (previousFocusRef.current && typeof previousFocusRef.current.focus === 'function') {
        previousFocusRef.current.focus();
      }
    };
  }, [onClose]);

  return (
    <div className="um-modal-overlay" onClick={onClose}>
      <div
        ref={modalRef}
        className="um-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="um-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="um-modal-header">
          <h2 id="um-modal-title"><FaCodeBranch /> Review &amp; Confirm Merge</h2>
          <button
            type="button"
            className="um-modal-close"
            onClick={onClose}
            aria-label="Close merge modal"
          >
            <FaTimes />
          </button>
        </div>

        <div className="um-modal-body">
          {/* Summary Cards */}
          <div className="um-merge-summary-cards">
            <div className="um-merge-card um-merge-card-update">
              <div className="um-merge-card-value">{mergeSummary.updates.length}</div>
              <div className="um-merge-card-label">Parameters to Update</div>
            </div>
            <div className="um-merge-card um-merge-card-add">
              <div className="um-merge-card-value">{mergeSummary.additions.length}</div>
              <div className="um-merge-card-label">New Parameters to Add</div>
            </div>
            <div className="um-merge-card um-merge-card-same">
              <div className="um-merge-card-value">{mergeSummary.unchanged.length}</div>
              <div className="um-merge-card-label">Unchanged</div>
            </div>
          </div>

          {/* Updates Detail */}
          {mergeSummary.updates.length > 0 && (
            <div className="um-merge-detail-section">
              <h4 className="um-merge-detail-title um-merge-detail-update">🔄 Parameters Being Updated</h4>
              <div className="um-merge-detail-list">
                {mergeSummary.updates.map((item, i) => (
                  <div key={i} className="um-merge-detail-row">
                    <span className="um-merge-detail-name">{item.name}</span>
                    <span className="um-merge-detail-change">
                      <span className="um-merge-old-id">{item.oldId}</span>
                      <span className="um-merge-arrow">→</span>
                      <span className="um-merge-new-id">{item.newId}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Additions Detail */}
          {mergeSummary.additions.length > 0 && (
            <div className="um-merge-detail-section">
              <h4 className="um-merge-detail-title um-merge-detail-add">✨ New Parameters Being Added</h4>
              <div className="um-merge-detail-list">
                {mergeSummary.additions.map((item, i) => (
                  <div key={i} className="um-merge-detail-row">
                    <span className="um-merge-detail-name">{item.name}</span>
                    <span className="um-merge-new-id">{item.newId}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No Changes */}
          {mergeSummary.updates.length === 0 && mergeSummary.additions.length === 0 && (
            <div className="um-merge-no-changes">
              <FaCheckCircle size={24} />
              <p>All local mappings already match the server. No changes needed.</p>
            </div>
          )}

          {/* Merge Error inside modal */}
          {mergeError && (
            <div className="um-merge-error-banner" style={{ marginTop: '1rem' }}>
              <FaExclamationTriangle />
              <span>{mergeError}</span>
            </div>
          )}
        </div>

        <div className="um-modal-footer">
          <button
            type="button"
            className="um-modal-cancel-btn"
            onClick={onClose}
            disabled={mergeLoading}
          >
            Cancel
          </button>
          {(mergeSummary.updates.length > 0 || mergeSummary.additions.length > 0) && (
            <button
              type="button"
              className="um-modal-confirm-btn"
              onClick={handleConfirmMerge}
              disabled={mergeLoading}
            >
              {mergeLoading ? (
                <><div className="um-validation-spinner" style={{ width: 16, height: 16, borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white' }}></div> Saving...</>
              ) : (
                <><FaCheckCircle /> Confirm &amp; Save</>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UnmappedManagement;


